import { useEffect, useState } from 'react';
import type { Theme } from '@ce/theme';
import { kicker, t } from '../styles';

/**
 * The launch splash.
 *
 * It paints from the *cached* theme immediately, so a returning attendee sees
 * their client's brand with no flash of an Anthropic-blank screen. The arc
 * motif is the fallback when a client has not uploaded splash art; a client
 * that has one gets it full-bleed behind the wordmark.
 *
 * It holds for `minDuration` so the brand actually registers, and then only
 * leaves once the bundle is ready — whichever is later.
 */
export function Splash({
  theme,
  ready,
  minDuration = 1100,
  onDone,
}: {
  theme: Theme;
  ready: boolean;
  minDuration?: number;
  onDone: () => void;
}) {
  const [held, setHeld] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setHeld(true), minDuration);
    return () => clearTimeout(id);
  }, [minDuration]);

  useEffect(() => {
    if (!held || !ready) return;
    setLeaving(true);
    const id = setTimeout(onDone, 420);
    return () => clearTimeout(id);
  }, [held, ready, onDone]);

  const brand = theme.brand;

  return (
    <div
      aria-hidden={leaving}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        background: brand.splashBackground,
        opacity: leaving ? 0 : 1,
        transform: leaving ? 'scale(1.04)' : 'none',
        transition: 'opacity .4s ease, transform .4s ease',
        pointerEvents: leaving ? 'none' : 'auto',
      }}
    >
      {brand.splashImageUrl ? (
        <img
          src={brand.splashImageUrl}
          alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', mixBlendMode: 'lighten', opacity: 0.9,
          }}
        />
      ) : (
        <SplashArcs theme={theme} />
      )}

      <div style={{ position: 'relative', padding: '0 30px' }}>
        {brand.logoUrl ? (
          <img
            src={brand.logoUrl}
            alt={brand.name}
            style={{ maxWidth: 220, maxHeight: 96, animation: 'splashIn .7s ease both' }}
          />
        ) : (
          <div style={{ animation: 'splashIn .7s ease both' }}>
            <div style={{ ...kicker(10, theme.colors.accentRamp[300], 0.24) }}>
              {theme.brand.tagline}
            </div>
            <div
              style={{
                font: `500 40px/1.02 var(--font-body)`,
                letterSpacing: '-0.035em',
                marginTop: 14,
                maxWidth: 260,
                color: theme.colors.text,
              }}
            >
              {brand.name}
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: 26,
            height: 2,
            width: 76,
            borderRadius: 2,
            overflow: 'hidden',
            background: 'rgba(var(--color-text-rgb), 0.12)',
          }}
        >
          <div
            style={{
              height: '100%',
              borderRadius: 2,
              background: theme.colors.accent,
              animation: 'splashBar 1.1s cubic-bezier(.4,0,.2,1) both',
            }}
          />
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 30,
          right: 30,
          bottom: 46,
          ...kicker(9, t(0.32), 0.2),
        }}
      >
        Powered by Convention Engine
      </div>
    </div>
  );
}

/** The Nocturne arc field, used as the default splash ground. */
function SplashArcs({ theme }: { theme: Theme }) {
  const ring = (size: number, opacity: number, top: number, delay: number) => (
    <div
      key={`${size}-${top}`}
      style={{
        position: 'absolute',
        left: '50%',
        top,
        width: size,
        height: size,
        marginLeft: -size / 2,
        borderRadius: '50%',
        border: `1px solid ${hexA(theme.colors.accentRamp[300], opacity)}`,
        animation: `splashRing 1.4s cubic-bezier(.2,.8,.2,1) ${delay}ms both`,
      }}
    />
  );

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(165deg, ${theme.colors.section} 0%, #1c1e33 62%, ${theme.colors.bg} 100%)`,
        }}
      />
      {ring(520, 0.22, -110, 0)}
      {ring(388, 0.16, -44, 110)}
      {ring(256, 0.12, 22, 220)}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: -20,
          width: 300,
          height: 300,
          marginLeft: -150,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${hexA(theme.colors.accentRamp[400], 0.3)}, transparent 65%)`,
        }}
      />
    </div>
  );
}

function hexA(hex: string, a: number) {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((x) => x + x).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
